import React, { useEffect, useState } from 'react';
import { expenseApi } from '../api/expense';
import { coaApi } from '../api/coa';
import { purchaseApi } from '../api/purchase';
import ContactPicker from '../components/ContactPicker';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

const Expenses: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [mode, setMode] = useState<'list'|'new'|'petty'|'recurring'>('list');
  const [rows, setRows] = useState<any[]>([]);
  const [cats, setCats] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [recur, setRecur] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(0);
  const [myLimit, setMyLimit] = useState<number|null>(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const blank = { expense_date: today, expense_account_id:'', account_id:'', amount:'', description:'', payee:'', reference:'', contact_id:'', contact_name:'', recurring_id:'', branch_id:'' };
  const [form, setForm] = useState<any>(blank);
  const blankTransfer = { date: today, from_account_id:'', is_cheque:false, cheque_no:'', payee:'', note:'',
    splits:[{ account_id:'', branch_id:'', amount:'', note:'' }] };
  const [petty, setPetty] = useState<any>(blankTransfer);
  const blankRec = { name:'', description:'', frequency:'monthly', day_of_month:1, next_due: today, payee:'', branch_id:'', contact_id:'', contact_name:'',
    lines: [{ account_id:'', branch_id:'', debit:'', credit:'', description:'' }, { account_id:'', branch_id:'', debit:'', credit:'', description:'' }] };
  const [rec, setRec] = useState<any>(blankRec);
  const [posting, setPosting] = useState<any>(null);   // the entry being posted

  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  const cash = accounts.filter(a => String(a.account_code).startsWith('1') && Number(a.account_code) < 1100);
  const payAccts = petty.is_cheque ? accounts : cash;
  const transferTotal = petty.splits.reduce((s:number,l:any) => s + Number(l.amount || 0), 0);

  const load = () => {
    setLoading(true);
    expenseApi.list({ status: status || undefined }).then(r => {
      setRows(r.data.data || []); setPending(r.data.pending_count || 0); setMyLimit(r.data.my_limit);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    expenseApi.categories().then(r => setCats(r.data.data || [])).catch(() => {});
    coaApi.list().then(r => setAccounts(r.data.data || [])).catch(() => {});
    expenseApi.recurring().then(r => setRecur(r.data.data || [])).catch(() => {});
    purchaseApi.branches().then(r => setBranches(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(load, [status]); // eslint-disable-line

  const save = async () => {
    if (!form.expense_account_id || !form.account_id || !form.amount || !form.description.trim()) {
      showToast('Category, account, amount and description are all needed', 'error'); return;
    }
    setSaving(true);
    try {
      const r = await expenseApi.create({ ...form,
        amount: Number(form.amount),
        contact_id: form.contact_id ? Number(form.contact_id) : null,
        recurring_id: form.recurring_id ? Number(form.recurring_id) : null });
      showToast(r.data.message);
      setForm(blank); setMode('list'); load();
      expenseApi.recurring().then(x => setRecur(x.data.data || []));
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const approve = async (e:any) => {
    if (!window.confirm('Approve ' + e.expense_no + ' for ' + fmt(e.amount) + '?')) return;
    try { await expenseApi.approve(e.id); showToast('Approved and posted'); load(); }
    catch (err:any) { showToast(err.response?.data?.message || 'Could not approve', 'error'); }
  };

  const reject = async (e:any) => {
    const reason = window.prompt('Why is ' + e.expense_no + ' being rejected?');
    if (!reason) return;
    try { await expenseApi.reject(e.id, reason); showToast('Rejected'); load(); }
    catch (err:any) { showToast(err.response?.data?.message || 'Could not reject', 'error'); }
  };

  const saveTransfer = async () => {
    if (!petty.from_account_id) { showToast('Choose the account the money comes from', 'error'); return; }
    const splits = petty.splits.filter((s:any) => s.account_id && Number(s.amount) > 0)
      .map((s:any) => ({ account_id: Number(s.account_id), branch_id: s.branch_id ? Number(s.branch_id) : null, amount: Number(s.amount), note: s.note || null }));
    if (!splits.length) { showToast('Add at least one line with an account and amount', 'error'); return; }
    if (petty.is_cheque && !petty.cheque_no.trim()) { showToast('Enter the cheque number', 'error'); return; }
    setSaving(true);
    try {
      const r = await expenseApi.replenish({ date: petty.date, from_account_id: Number(petty.from_account_id),
        is_cheque: !!petty.is_cheque, cheque_no: petty.cheque_no || null, payee: petty.payee || null, note: petty.note || null, splits });
      showToast(r.data.message || 'Posted'); setPetty(blankTransfer); setMode('list');
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const recDr = (ls:any[]) => ls.reduce((s,l) => s + Number(l.debit || 0), 0);
  const recCr = (ls:any[]) => ls.reduce((s,l) => s + Number(l.credit || 0), 0);

  const saveRec = async () => {
    if (!rec.name.trim() || !rec.next_due) { showToast('A name and the next due date are needed', 'error'); return; }
    const lines = rec.lines.filter((l:any) => l.account_id && (Number(l.debit) || Number(l.credit)))
      .map((l:any) => ({ account_id: Number(l.account_id), branch_id: l.branch_id ? Number(l.branch_id) : null, debit: Number(l.debit || 0), credit: Number(l.credit || 0), description: l.description || null }));
    if (lines.length < 2) { showToast('At least two lines are needed', 'error'); return; }
    if (Math.abs(recDr(lines) - recCr(lines)) > 0.009) { showToast('Debits and credits must agree', 'error'); return; }
    try {
      await expenseApi.saveRecurring({ ...rec, lines,
        contact_id: rec.contact_id ? Number(rec.contact_id) : null,
        branch_id: rec.branch_id ? Number(rec.branch_id) : null });
      showToast('Saved'); setRec(blankRec);
      expenseApi.recurring().then(x => setRecur(x.data.data || []));
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
  };

  const openPost = (r:any) => setPosting({ ...r, entry_date: today, note:'',
    lines: (r.lines || []).map((l:any) => ({ ...l, debit: String(l.debit || ''), credit: String(l.credit || '') })) });

  const doPost = async () => {
    const lines = posting.lines.filter((l:any) => l.account_id && (Number(l.debit) || Number(l.credit)))
      .map((l:any) => ({ account_id: Number(l.account_id), branch_id: l.branch_id ? Number(l.branch_id) : null, debit: Number(l.debit || 0), credit: Number(l.credit || 0), description: l.description || null }));
    if (Math.abs(recDr(lines) - recCr(lines)) > 0.009) { showToast('Debits and credits must agree', 'error'); return; }
    setSaving(true);
    try {
      const r = await expenseApi.postRecurring(posting.id, { entry_date: posting.entry_date, note: posting.note || null, lines });
      showToast(r.data.message); setPosting(null);
      expenseApi.recurring().then(x => setRecur(x.data.data || []));
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not post', 'error'); }
    finally { setSaving(false); }
  };

  const enterRecurring = (r:any) => { openPost(r); setMode('recurring'); };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' };
  const badge = (s:string) => ({ pending:'badge-amber', posted:'badge-green', rejected:'badge-red', approved:'badge-blue' } as any)[s] || 'badge-blue';
  const dueNow = recur.filter((r:any) => r.due);

  // ---------- record ----------
  if (mode === 'new') return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Record an Expense</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => { setForm(blank); setMode('list'); }}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      {myLimit !== null && (
        <div style={{ fontSize:12, color:'var(--tx3)', marginBottom:10 }}>
          You can approve up to {fmt(myLimit)}. Anything above that will wait for approval.
        </div>
      )}
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Date *</label>
            <input type="date" className="form-control" value={form.expense_date} onChange={e => setForm({...form, expense_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">What for? *</label>
            <select className="form-control" value={form.expense_account_id} onChange={e => setForm({...form, expense_account_id:e.target.value})}>
              <option value="">Choose an expense account</option>
              {cats.map(c => <option key={c.account_id} value={c.account_id}>{c.account_code} {c.name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Paid from *</label>
            <select className="form-control" value={form.account_id} onChange={e => setForm({...form, account_id:e.target.value})}>
              <option value="">Choose an account</option>
              {cash.map(a => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Amount *</label>
            <input className="form-control mono" style={{textAlign:'right'}} value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Branch</label>
            <select className="form-control" value={form.branch_id} onChange={e => setForm({...form, branch_id:e.target.value})}>
              <option value="">Default</option>
              {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
        </div>
        <div className="form-group"><label className="form-label">Description *</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e => setForm({...form, description:e.target.value})} /></div>
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Paid to (a supplier)</label>
            <ContactPicker value={form.contact_id} initialLabel={form.contact_name}
              placeholder="Leave empty if not a registered supplier"
              onSelect={(c:any) => setForm({...form, contact_id:String(c.id), contact_name:c.name})} /></div>
          <div className="form-group"><label className="form-label">Or just a name</label>
            <input className="form-control" placeholder="Bajaaj driver, landlord..." value={form.payee} onChange={e => setForm({...form, payee:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Receipt number</label>
            <input className="form-control mono" value={form.reference} onChange={e => setForm({...form, reference:e.target.value})} /></div>
        </div>
      </div></div>
    </div>
  );

  // ---------- transfer cash / write cheque ----------
  if (mode === 'petty') return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Transfer Cash</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => { setPetty(blankTransfer); setMode('list'); }}>Cancel</button>
          <button className="btn btn-primary" onClick={saveTransfer} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Date *</label>
            <input type="date" className="form-control" value={petty.date} onChange={e => setPetty({...petty, date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">{petty.is_cheque ? 'Paid from (any account) *' : 'Taken from *'}</label>
            <select className="form-control" value={petty.from_account_id} onChange={e => setPetty({...petty, from_account_id:e.target.value})}>
              <option value="">Choose an account</option>
              {payAccts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
            </select></div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, margin:'4px 0 10px' }}>
          <input type="checkbox" checked={!!petty.is_cheque} onChange={e => setPetty({...petty, is_cheque:e.target.checked, from_account_id:''})} />
          Write a cheque (lets you pick any account, not just cash)
        </label>
        {petty.is_cheque && (
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Cheque number *</label>
              <input className="form-control mono" value={petty.cheque_no} onChange={e => setPetty({...petty, cheque_no:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Payee</label>
              <input className="form-control" placeholder="Who the cheque is to" value={petty.payee} onChange={e => setPetty({...petty, payee:e.target.value})} /></div>
          </div>
        )}
        <div style={{ fontSize:12, fontWeight:600, margin:'10px 0 4px' }}>Where it goes</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>Account</th><th style={th}>Branch</th><th style={{...th, textAlign:'right'}}>Amount</th><th style={th}>Note</th><th></th>
          </tr></thead>
          <tbody>
            {petty.splits.map((l:any, i:number) => (
              <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'4px 6px' }}>
                  <select className="form-control" style={{fontSize:12}} value={l.account_id}
                    onChange={e => setPetty({...petty, splits: petty.splits.map((x:any,j:number) => j===i ? {...x, account_id:e.target.value} : x)})}>
                    <option value="">Choose</option>
                    {payAccts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                  </select></td>
                <td style={{ padding:'4px 6px', width:150 }}>
                  <select className="form-control" style={{fontSize:12}} value={l.branch_id || ''}
                    onChange={e => setPetty({...petty, splits: petty.splits.map((x:any,j:number) => j===i ? {...x, branch_id:e.target.value} : x)})}>
                    <option value="">Default</option>
                    {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select></td>
                <td style={{ padding:'4px 6px', width:120 }}>
                  <input className="form-control mono" style={{textAlign:'right'}} value={l.amount}
                    onChange={e => setPetty({...petty, splits: petty.splits.map((x:any,j:number) => j===i ? {...x, amount:e.target.value} : x)})} /></td>
                <td style={{ padding:'4px 6px' }}>
                  <input className="form-control" style={{fontSize:12}} value={l.note || ''}
                    onChange={e => setPetty({...petty, splits: petty.splits.map((x:any,j:number) => j===i ? {...x, note:e.target.value} : x)})} /></td>
                <td style={{ padding:'4px 6px', textAlign:'right' }}>
                  {petty.splits.length > 1 && <button style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:16 }}
                    onClick={() => setPetty({...petty, splits: petty.splits.filter((_:any,j:number) => j!==i)})}>&times;</button>}</td>
              </tr>
            ))}
            <tr style={{ background:'var(--sur2)', fontWeight:700 }}>
              <td style={{ padding:'8px 10px', fontSize:12 }}>Total</td>
              <td></td>
              <td className="td-num">{fmt(transferTotal)}</td>
              <td></td><td></td>
            </tr>
          </tbody>
        </table>
        <div style={{ display:'flex', gap:8, marginTop:10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPetty({...petty, splits:[...petty.splits, { account_id:'', branch_id:'', amount:'', note:'' }]})}>+ Add line</button>
        </div>
        <div className="form-group" style={{ marginTop:10 }}><label className="form-label">Note</label>
          <input className="form-control" value={petty.note} onChange={e => setPetty({...petty, note:e.target.value})} /></div>
      </div></div>
    </div>
  );

  // ---------- memorized transactions ----------
  if (mode === 'recurring') return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{posting ? 'Post — ' + posting.name : 'Memorized Transactions'}</h1>
        <div style={{ display:'flex', gap:8 }}>
          {posting
            ? <>
                <button className="btn btn-secondary" onClick={() => setPosting(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={doPost} disabled={saving}>{saving ? 'Posting...' : 'Post it'}</button>
              </>
            : <button className="btn btn-secondary" onClick={() => setMode('list')}>Back</button>}
        </div>
      </div>

      {posting ? (
        <div className="card"><div className="card-body">
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Date</label>
              <input type="date" className="form-control" value={posting.entry_date} onChange={e => setPosting({...posting, entry_date:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Note</label>
              <input className="form-control" placeholder="October rent" value={posting.note} onChange={e => setPosting({...posting, note:e.target.value})} /></div>
          </div>
          <div style={{ fontSize:11, color:'var(--tx3)', margin:'4px 0 8px' }}>Each branch posts as its own journal entry, so a branch's lines must balance on their own.</div>
          <table style={{ width:'100%', borderCollapse:'collapse', marginTop:8 }}>
            <thead><tr style={{ background:'var(--sur2)' }}>
              <th style={th}>Account</th><th style={th}>Branch</th><th style={{...th, textAlign:'right'}}>Debit</th><th style={{...th, textAlign:'right'}}>Credit</th><th style={th}>Note</th>
            </tr></thead>
            <tbody>
              {posting.lines.map((l:any, i:number) => (
                <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                  <td style={{ padding:'4px 6px' }}>
                    <select className="form-control" style={{fontSize:12}} value={l.account_id}
                      onChange={e => setPosting({...posting, lines: posting.lines.map((x:any,j:number) => j===i ? {...x, account_id:e.target.value} : x)})}>
                      <option value="">Choose</option>
                      {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                    </select></td>
                  <td style={{ padding:'4px 6px', width:150 }}>
                    <select className="form-control" style={{fontSize:12}} value={l.branch_id || ''}
                      onChange={e => setPosting({...posting, lines: posting.lines.map((x:any,j:number) => j===i ? {...x, branch_id:e.target.value} : x)})}>
                      <option value="">Default</option>
                      {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select></td>
                  <td style={{ padding:'4px 6px', width:110 }}>
                    <input className="form-control mono" style={{textAlign:'right'}} value={l.debit}
                      onChange={e => setPosting({...posting, lines: posting.lines.map((x:any,j:number) => j===i ? {...x, debit:e.target.value, credit:''} : x)})} /></td>
                  <td style={{ padding:'4px 6px', width:110 }}>
                    <input className="form-control mono" style={{textAlign:'right'}} value={l.credit}
                      onChange={e => setPosting({...posting, lines: posting.lines.map((x:any,j:number) => j===i ? {...x, credit:e.target.value, debit:''} : x)})} /></td>
                  <td style={{ padding:'4px 6px' }}>
                    <input className="form-control" style={{fontSize:12}} value={l.description || ''}
                      onChange={e => setPosting({...posting, lines: posting.lines.map((x:any,j:number) => j===i ? {...x, description:e.target.value} : x)})} /></td>
                </tr>
              ))}
              <tr style={{ background:'var(--sur2)', fontWeight:700 }}>
                <td style={{ padding:'8px 10px', fontSize:12 }}>Total</td>
                <td></td>
                <td className="td-num">{fmt(recDr(posting.lines))}</td>
                <td className="td-num">{fmt(recCr(posting.lines))}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color: Math.abs(recDr(posting.lines)-recCr(posting.lines)) < 0.01 ? 'var(--green)' : 'var(--red)' }}>
                  {Math.abs(recDr(posting.lines)-recCr(posting.lines)) < 0.01 ? 'balanced' : 'does not balance'}</td>
              </tr>
            </tbody>
          </table>
        </div></div>
      ) : (
        <>
          <div className="card" style={{ marginBottom:12 }}>
            <div className="card-header"><span>Set up a new one</span></div>
            <div className="card-body">
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Name *</label>
                  <input className="form-control" placeholder="Shop rent, depreciation..." value={rec.name} onChange={e => setRec({...rec, name:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">How often</label>
                  <select className="form-control" value={rec.frequency} onChange={e => setRec({...rec, frequency:e.target.value})}>
                    <option value="monthly">Every month</option><option value="weekly">Every week</option><option value="yearly">Every year</option>
                  </select></div>
                <div className="form-group"><label className="form-label">Next due *</label>
                  <input type="date" className="form-control" value={rec.next_due} onChange={e => setRec({...rec, next_due:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Default branch</label>
                  <select className="form-control" value={rec.branch_id} onChange={e => setRec({...rec, branch_id:e.target.value})}>
                    <option value="">Default</option>
                    {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select></div>
                <div className="form-group"><label className="form-label">Paid to (a contact)</label>
                  <ContactPicker value={rec.contact_id} initialLabel={rec.contact_name}
                    placeholder="Leave empty if not a registered contact"
                    onSelect={(ct:any) => setRec({...rec, contact_id:String(ct.id), contact_name:ct.name})} /></div>
                <div className="form-group"><label className="form-label">Or just a name</label>
                  <input className="form-control" placeholder="Landlord, driver..." value={rec.payee} onChange={e => setRec({...rec, payee:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Description</label>
                  <input className="form-control" value={rec.description} onChange={e => setRec({...rec, description:e.target.value})} /></div>
              </div>

              <div style={{ fontSize:12, fontWeight:600, margin:'10px 0 4px' }}>What it posts</div>
              <div style={{ fontSize:11, color:'var(--tx3)', marginBottom:6 }}>Set a branch per line when the amount belongs to a shop. Leave it on Default to use the default branch above.</div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:'var(--sur2)' }}>
                  <th style={th}>Account</th><th style={th}>Branch</th><th style={{...th, textAlign:'right'}}>Debit</th><th style={{...th, textAlign:'right'}}>Credit</th><th style={th}>Note</th><th></th>
                </tr></thead>
                <tbody>
                  {rec.lines.map((l:any, i:number) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'4px 6px' }}>
                        <select className="form-control" style={{fontSize:12}} value={l.account_id}
                          onChange={e => setRec({...rec, lines: rec.lines.map((x:any,j:number) => j===i ? {...x, account_id:e.target.value} : x)})}>
                          <option value="">Choose</option>
                          {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                        </select></td>
                      <td style={{ padding:'4px 6px', width:150 }}>
                        <select className="form-control" style={{fontSize:12}} value={l.branch_id || ''}
                          onChange={e => setRec({...rec, lines: rec.lines.map((x:any,j:number) => j===i ? {...x, branch_id:e.target.value} : x)})}>
                          <option value="">Default</option>
                          {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select></td>
                      <td style={{ padding:'4px 6px', width:110 }}>
                        <input className="form-control mono" style={{textAlign:'right'}} value={l.debit}
                          onChange={e => setRec({...rec, lines: rec.lines.map((x:any,j:number) => j===i ? {...x, debit:e.target.value, credit:''} : x)})} /></td>
                      <td style={{ padding:'4px 6px', width:110 }}>
                        <input className="form-control mono" style={{textAlign:'right'}} value={l.credit}
                          onChange={e => setRec({...rec, lines: rec.lines.map((x:any,j:number) => j===i ? {...x, credit:e.target.value, debit:''} : x)})} /></td>
                      <td style={{ padding:'4px 6px' }}>
                        <input className="form-control" style={{fontSize:12}} value={l.description || ''}
                          onChange={e => setRec({...rec, lines: rec.lines.map((x:any,j:number) => j===i ? {...x, description:e.target.value} : x)})} /></td>
                      <td style={{ padding:'4px 6px', textAlign:'right' }}>
                        {rec.lines.length > 2 && <button style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:16 }}
                          onClick={() => setRec({...rec, lines: rec.lines.filter((_:any,j:number) => j!==i)})}>&times;</button>}</td>
                    </tr>
                  ))}
                  <tr style={{ background:'var(--sur2)', fontWeight:700 }}>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>Total</td>
                    <td></td>
                    <td className="td-num">{fmt(recDr(rec.lines))}</td>
                    <td className="td-num">{fmt(recCr(rec.lines))}</td>
                    <td style={{ padding:'8px 10px', fontSize:11, color: Math.abs(recDr(rec.lines)-recCr(rec.lines)) < 0.01 ? 'var(--green)' : 'var(--red)' }}>
                      {Math.abs(recDr(rec.lines)-recCr(rec.lines)) < 0.01 ? 'balanced' : 'does not balance'}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setRec({...rec, lines:[...rec.lines, { account_id:'', branch_id:'', debit:'', credit:'', description:'' }]})}>+ Add line</button>
                <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={saveRec}>Save</button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span>Set up</span><span className="record-count">{recur.length}</span></div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={th}>Name</th><th style={{...th, textAlign:'right'}}>Amount</th><th style={th}>How often</th>
                <th style={th}>Next due</th><th style={th}>Last entered</th><th style={th}>Branch</th><th></th>
              </tr></thead>
              <tbody>
                {recur.length === 0 ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>None set up.</td></tr>
                : recur.map((r:any) => (
                  <tr key={r.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600 }}>{r.name}</td>
                    <td className="td-num" style={{ padding:'8px 10px' }}>{Number(r.amount) ? fmt(r.amount) : '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{r.frequency}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color: r.due ? 'var(--red)' : 'var(--tx3)', fontWeight: r.due ? 600 : 400 }}>
                      {r.next_due}{r.due ? ' · due' : ''}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{r.last_entered || 'never'}</td>
                    <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{r.branch_name || '-'}</td>
                    <td style={{ padding:'6px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => openPost(r)}>Post</button>
                      <button className="btn btn-secondary btn-sm" style={{marginLeft:6}}
                        onClick={async () => { if (window.confirm('Stop ' + r.name + '?')) { await expenseApi.stopRecurring(r.id); expenseApi.recurring().then(x => setRecur(x.data.data || [])); } }}>Stop</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  // ---------- the list ----------
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Expenses</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setMode('recurring')}>
            Memorized{dueNow.length ? ' (' + dueNow.length + ' due)' : ''}
          </button>
          {hasPermission('expenses.create') && <button className="btn btn-secondary" onClick={() => setMode('petty')}>Transfer Cash</button>}
          {hasPermission('expenses.create') && <button className="btn btn-primary" onClick={() => { setForm(blank); setMode('new'); }}>+ New Expense</button>}
        </div>
      </div>

      {dueNow.length > 0 && (
        <div className="card" style={{ marginBottom:12, borderLeft:'3px solid var(--amber)' }}>
          <div className="card-body" style={{ padding:'10px 14px' }}>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Due to be entered</div>
            {dueNow.map((r:any) => (
              <div key={r.id} style={{ display:'flex', alignItems:'center', gap:10, fontSize:12, padding:'3px 0' }}>
                <span style={{ minWidth:160 }}>{r.name}</span>
                <span style={{ color:'var(--tx3)' }}>due {r.next_due}</span>
                <span className="mono">{Number(r.amount) ? fmt(r.amount) : ''}</span>
                <button className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }} onClick={() => enterRecurring(r)}>Enter it</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom:12, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
        {[['','All'],['pending','Waiting'],['posted','Posted'],['rejected','Rejected']].map(([v,l]) => (
          <button key={v} className={'btn btn-sm ' + (status===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setStatus(v)}>
            {l}{v==='pending' && pending ? ' (' + pending + ')' : ''}
          </button>
        ))}
        <span className="record-count" style={{ marginLeft:'auto' }}>{rows.length}</span>
      </div>

      <div className="card">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>No</th><th style={th}>Date</th><th style={th}>Category</th><th style={th}>Description</th>
            <th style={th}>Paid to</th><th style={th}>From</th><th style={{...th, textAlign:'right'}}>Amount</th>
            <th style={th}>Status</th><th style={th}>By</th><th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={10} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={10} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>No expenses.</td></tr>
            : rows.map((e:any) => (
              <tr key={e.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600, color:'var(--blue)' }}>{e.expense_no}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }}>{e.expense_date}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }}>{e.category_name}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx2)' }}>
                  {String(e.description).slice(0,40)}
                  {e.reject_reason && <div style={{ fontSize:11, color:'var(--red)' }}>Rejected: {e.reject_reason}</div>}
                </td>
                <td style={{ padding:'8px 10px', fontSize:12 }}>{e.contact_name || e.payee || '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{e.account_code}</td>
                <td className="td-num" style={{ padding:'8px 10px', fontWeight:600 }}>{fmt(e.amount)}</td>
                <td style={{ padding:'8px 10px' }}><span className={'badge ' + badge(e.status)}>{e.status}</span></td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{e.requested_name}</td>
                <td style={{ padding:'6px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                  {e.status === 'pending' && hasPermission('expenses.approve') && <>
                    <button className="btn btn-primary btn-sm" onClick={() => approve(e)}>Approve</button>
                    <button className="btn btn-secondary btn-sm" style={{marginLeft:6}} onClick={() => reject(e)}>Reject</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Expenses;
